import { DateUtils } from "src/app/shared/util/date-utils";
import { Credor } from "./credor";
import { Titular } from "./titular";
import { Arquivo } from "./arquivo";

interface Magistrado {
  id: number;
  nome: string;
  documento: string;
}

interface Cadastrador {
  documento: string;
  nome: string;
}

interface Foro {
  id: number;
  nome: string;
}

interface Praca {
  sigla: string;
  nome: string;
  uf: string;
}

interface Vara {
  id: number;
  nome: string;
  numero: number;
}

export interface SubEsfera {
  id: number;
  nome: string;
}

export interface Esfera {
  id: number;
  nome: string;
}

interface DadosOrdemAcao {
  valorAnotacao: number;
  nomeAutor: string;
  nomeReu: string;
  nomeCredor: string;
  cartaComunicado: boolean;
}

interface DadosOrdemBaixa {
  credores: Credor[];
  nomeAutor: string;
  nomeReu: string;
}

interface DadosOrdemHistorico {
  periodoInicial: string;
  periodoFinal: string;
}

interface DadosOrdemEndereco {
}

interface DadosOrdemRevogacao {
  nomeAutor: string;
  nomeReu: string;
  descricaoOrdem: string;
}

interface DadosOrdemOutros {
  arquivo: Arquivo,
  descricaoOrdem: string;
}

export class OrdemSolicitacao {
  public numeroProcesso: string;
  public titulares: Titular[];
  public horasSla: number;
  public magistrado: Magistrado;
  public cadastrador: Cadastrador;
  public foro: Foro;
  public praca: Praca;
  public vara: Vara;
  public esfera: Esfera;
  public subEsfera: SubEsfera;
  public tipoOrdem: string;

  static buildBase(nroProcesso: string, titulares: Titular[], form: any, usuario: any, tipoOrdem: string): OrdemSolicitacao {
    const ordem = new OrdemSolicitacao();
    ordem.numeroProcesso = nroProcesso;
    ordem.titulares = this.processarTitulares(titulares, tipoOrdem);
    ordem.horasSla = form.nroSla;
    ordem.magistrado = {
      id: form.vara.idMagistradoVara,
      nome: form.vara.nomeMagistrado,
      documento: form.vara.numeroDocumento
    };
    ordem.cadastrador = {
      documento: usuario.document?.documentId,
      nome: usuario?.fullName
    };
    ordem.foro = {
      id: form.foro.idForo,
      nome: form.foro.descricaoForo
    };
    ordem.praca = {
      sigla: form.vara.siglaPraca,
      nome: form.vara.nomePraca,
      uf: form.vara.uf
    };
    ordem.vara = {
      id: form.vara.idVara,
      nome: form.vara.descricaoVara,
      numero: form.vara.numeroVara
    };
    ordem.esfera = form.vara.esfera;
    ordem.subEsfera = form.vara.subEsfera;
    ordem.tipoOrdem = tipoOrdem;
    return ordem;
  }

  public static processarTitulares(titulares: Titular[], tipoOrdem: string): Titular[] {
    if (tipoOrdem === "ACAO" && titulares.length > 0) {
      return titulares.map((titular, index) => ({
        ...titular,
        principal: index === 0
      }));
    }
    return titulares;
  }
}

export class OrdemInclusao extends OrdemSolicitacao {
  public dadosOrdem: DadosOrdemAcao;

  constructor(nroProcesso: string, titulares: Titular[], form: any, usuario: any) {
    super();
    Object.assign(this, OrdemSolicitacao.buildBase(nroProcesso, titulares, form, usuario, "ACAO"));
    this.dadosOrdem = {
      valorAnotacao: form.vlrAcao,
      nomeAutor: form.autor,
      nomeReu: form.reu,
      nomeCredor: form.autor,
      cartaComunicado: form.cartaComunicado
    };
  }

  static build(nroProcesso: string, titulares: Titular[], form: any, usuario: any): OrdemInclusao {
    return new OrdemInclusao(nroProcesso, titulares, form, usuario);
  }
}

export class OrdemBaixa extends OrdemSolicitacao {
  public dadosOrdem: DadosOrdemBaixa;

  constructor(nroProcesso: string, titulares: Titular[], form: any, usuario: any, credores: Credor[]) {
    super();
    Object.assign(this, OrdemSolicitacao.buildBase(nroProcesso, titulares, form, usuario, "BAIXA"));
    this.dadosOrdem = {
      credores: credores,
      nomeAutor: form.autor,
      nomeReu: form.reu,
    };
  }

  static build(nroProcesso: string, titulares: Titular[], form: any, usuario: any, credores: Credor[]): OrdemBaixa {
    return new OrdemBaixa(nroProcesso, titulares, form, usuario, credores);
  }
}

export class OrdemHistorico extends OrdemSolicitacao {
  public dadosOrdem: DadosOrdemHistorico;

  constructor(nroProcesso: string, titulares: Titular[], form: any, usuario: any) {
    super();
    Object.assign(this, OrdemSolicitacao.buildBase(nroProcesso, titulares, form, usuario, "HISTORICO"));
    let initDate = new Date();
    let finalDate = new Date();
    let subtractedDate = DateUtils.minusYears(initDate, form.anosHistorico);
    let initDateTimeParts = DateUtils.truncate(subtractedDate);
    let finalDateTimeParts = DateUtils.truncate(finalDate);
    this.dadosOrdem = {
      periodoInicial: initDateTimeParts[0],
      periodoFinal: finalDateTimeParts[0]
    };
  }

  static build(nroProcesso: string, titulares: Titular[], form: any, usuario: any): OrdemHistorico {
    return new OrdemHistorico(nroProcesso, titulares, form, usuario);
  }
}

export class OrdemEndereco extends OrdemSolicitacao {
  public dadosOrdem: DadosOrdemEndereco;

  constructor(nroProcesso: string, titulares: Titular[], form: any, usuario: any) {
    super();
    Object.assign(this, OrdemSolicitacao.buildBase(nroProcesso, titulares, form, usuario, "CEND"));
    this.dadosOrdem = {};
  }

  static build(nroProcesso: string, titulares: Titular[], form: any, usuario: any): OrdemEndereco {
    return new OrdemEndereco(nroProcesso, titulares, form, usuario);
  }
}

export class OrdemRevogacao extends OrdemSolicitacao {
  public dadosOrdem: DadosOrdemRevogacao;
  public static DESCRICAO_ORDEM = "Considerando a revogação da decisão judicial anteriormente proferida nestes autos, determino que a base de dados da Serasa Experian seja restabelecida à sua condição original.";

  constructor(nroProcesso: string, titulares: Titular[], form: any, usuario: any) {
    super();
    Object.assign(this, OrdemSolicitacao.buildBase(nroProcesso, titulares, form, usuario, "REVOGACAO"));
    this.dadosOrdem = {
      nomeAutor: form.autor,
      nomeReu: form.reu,
      descricaoOrdem: OrdemRevogacao.DESCRICAO_ORDEM,
    };
  }

  static build(nroProcesso: string, titulares: Titular[], form: any, usuario: any): OrdemRevogacao {
    return new OrdemRevogacao(nroProcesso, titulares, form, usuario);
  }
}

export class OrdemOutros extends OrdemSolicitacao {
  public dadosOrdem: DadosOrdemOutros;

  constructor(nroProcesso: string, titulares: Titular[], form: any, usuario: any, arquivo: Arquivo) {
    super();
    Object.assign(this, OrdemSolicitacao.buildBase(nroProcesso, titulares, form, usuario, "OUTROS"));
    this.dadosOrdem = {
      arquivo: arquivo,
      descricaoOrdem: form.descricaoOrdem,
    };
  }

  static build(nroProcesso: string, titulares: Titular[], form: any, usuario: any, arquivo: Arquivo): OrdemOutros {
    return new OrdemOutros(nroProcesso, titulares, form, usuario, arquivo);
  }
}
